import redirect from '@anm/helpers/redirect';
import useWindowWidthCheck from '@anm/hooks/useWindowWidthCheck';
import uploadsReducer, { uploadsSaga } from '@anm/store/modules/uploads';
import userDestinationsReducer, { userDestinationsSaga } from '@anm/store/modules/userDestinations';
import { SHOW_MOBILE_HEADER_BREAKPOINT } from '@anm/styles/constants';
import { NextInitialProps } from 'anm-next';
import { useCallback, useState, FC } from 'react';
import { compose } from 'recompose';
import bootstrapPage, { BootstrapPageProps } from '../HOCs/bootstrapPage';
import injectSagasAndReducers from '../HOCs/injectSagasAndReducers';
import withAuthGuard from '../HOCs/withAuthGuard';
import MetaHead from '../components/MetaHead';
import CustomizationSection from '../components/hosting/CustomizationSection';
import Layout, { LayoutProvider } from '../components/layouts/Layout';
import analyticsReducer, { analyticsDynamicSaga } from '../store/modules/analytics';
import brandsReducer, { brandsSaga } from '../store/modules/brands';
import collapseReducer, { collapseSaga } from '../store/modules/collapse';
import domainsReducer, { domainsSaga } from '../store/modules/domains';
import hostedVideoReducer, { hostedVideoSaga } from '../store/modules/hostedVideo';
import landingsReducer, { landingsSaga } from '../store/modules/landings';
import streamReducer, { streamSaga } from '../store/modules/stream';
import userPrefsReducer, { userPrefsSaga } from '../store/modules/userprefs';
import videoFolderReducer, { videoFolderSaga } from '../store/modules/videoFolder';

type HOCs = BootstrapPageProps;

type TabItem = {
  index: CustomizationTabIndexes;
  title: string;
};

type VideoPageProps = {
  currentTab: TabItem;
};

export const PREVIEW_TAB = 0;
export const LANDING_TAB = 1;
export const APPEARANCE_TAB = 2;
export const EMBED_TAB = 3;
export const PASSWORD_TAB = 4;
export const EMAIL_TAB = 5;
export const STATS_TAB = 6;
export const BROADCASTS_TAB = 7;
export const DOMAINS_TAB = 8;

export type CustomizationTabIndexes =
  | typeof PREVIEW_TAB
  | typeof APPEARANCE_TAB
  | typeof LANDING_TAB
  | typeof EMBED_TAB
  | typeof PASSWORD_TAB
  | typeof EMAIL_TAB
  | typeof STATS_TAB
  | typeof BROADCASTS_TAB
  | typeof DOMAINS_TAB;

export type DashboardTabs =
  | 'preview'
  | 'appearance'
  | 'landing'
  | 'embed'
  | 'permissions'
  | 'email-campaign'
  | 'stats'
  | 'broadcasts'
  | 'domains';

export const dashboardTabMap: {
  [key in CustomizationTabIndexes]: DashboardTabs;
} = {
  0: 'preview',
  1: 'landing',
  2: 'appearance',
  3: 'embed',
  4: 'permissions',
  5: 'email-campaign',
  6: 'stats',
  7: 'broadcasts',
  8: 'domains'
};

const VideoPage = ({ params: { projectId }, currentTab }: VideoPageProps & HOCs) => {
  const showMobileNavigation = useWindowWidthCheck(SHOW_MOBILE_HEADER_BREAKPOINT);
  const [activeTab, setActiveTab] = useState(currentTab);

  const handleTabChange = useCallback((tab: CustomizationTabIndexes) => {
    const selectedTab = (Object.keys(tabMap) as DashboardTabs[]).find((o: DashboardTabs) => tabMap[o].index === tab);
    const newTab = selectedTab && tabMap[selectedTab];

    newTab && setActiveTab(newTab);
  }, []);

  return (
    <>
      <MetaHead title={`${activeTab.title} | Wave.video`} />
      <LayoutProvider value={{ isNavigation: showMobileNavigation! }}>
        <Layout footerVariant="no-footer" navigationVariant="white-hosting" mainVariant="cover" hasShadow>
          <CustomizationSection projectId={projectId} activeTab={activeTab.index} onTabChange={handleTabChange} />
        </Layout>
      </LayoutProvider>
    </>
  );
};

export const tabMap: { [key in DashboardTabs]: TabItem } = {
  preview: {
    index: PREVIEW_TAB,
    title: 'Preview'
  },
  landing: {
    index: LANDING_TAB,
    title: 'Landing Page'
  },
  appearance: {
    index: APPEARANCE_TAB,
    title: 'Video Appearance'
  },
  embed: {
    index: EMBED_TAB,
    title: 'Embed Settings'
  },
  permissions: {
    index: PASSWORD_TAB,
    title: 'Password Settings'
  },
  'email-campaign': {
    index: EMAIL_TAB,
    title: 'Email Campaign'
  },
  stats: {
    index: STATS_TAB,
    title: 'Stats'
  },
  broadcasts: {
    index: BROADCASTS_TAB,
    title: 'Streams'
  },
  domains: {
    index: DOMAINS_TAB,
    title: 'Custom domain'
  }
};

VideoPage.getInitialProps = async (ctx: NextInitialProps<{ projectId: string; tab: DashboardTabs }>) => {
  const { projectId, tab } = ctx.query;
  const isTabExist = tabMap.hasOwnProperty(tab);

  if (!isTabExist) redirect(ctx, `/_error`);
  if (!tab) redirect(ctx, `/video/${projectId}/appearance`);

  const currentTab = tabMap[tab];
  return { currentTab };
};

export default compose(
  bootstrapPage,
  withAuthGuard({ redirectTo: 'login' }),
  injectSagasAndReducers(
    () => [
      ...analyticsDynamicSaga(),
      ...hostedVideoSaga(),
      ...videoFolderSaga(),
      ...landingsSaga(),
      ...collapseSaga(),
      ...brandsSaga(),
      ...streamSaga(),
      ...userPrefsSaga(),
      ...uploadsSaga(),
      ...userDestinationsSaga(),
      ...domainsSaga()
    ],
    {
      brands: brandsReducer,
      collapse: collapseReducer,
      landings: landingsReducer,
      analytics: analyticsReducer,
      videoFolder: videoFolderReducer,
      hostedVideo: hostedVideoReducer,
      stream: streamReducer,
      uploads: uploadsReducer,
      userPrefs: userPrefsReducer,
      domains: domainsReducer,
      userDestinations: userDestinationsReducer
    },
    { id: 'WebinarPage' }
  )
)(VideoPage as FC);
